// Feather "link" icon — a static, trusted constant (no user content ever
// interpolated here, so assigning it via innerHTML is safe).
const LINK_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>' +
  '<path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>'

const COPIED_RESET_MS = 1500

/**
 * Progressively enhances each <h2>/<h3> with an `id` inside `container` by
 * prepending a "copy link" button. Clicking it copies `<baseUrl>#<id>` to the
 * clipboard and briefly shows a "Copied!" tooltip.
 *
 * Runs client-side only (operates on already-rendered DOM). Idempotent, and
 * returns a cleanup function that removes the injected buttons + listeners.
 *
 * @param {HTMLElement|null} container - element wrapping the rendered post body
 * @param {string} baseUrl - the post's URL without a hash (e.g. https://…/blog/slug)
 * @returns {() => void} cleanup function
 */
export function enhanceHeadingAnchors(container, baseUrl) {
  if (!container) {
    return () => {}
  }

  const headings = container.querySelectorAll('h2[id], h3[id]')
  const cleanups = []

  headings.forEach((heading) => {
    if (heading.dataset.anchorEnhanced === 'true') {
      return
    }
    heading.dataset.anchorEnhanced = 'true'

    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'anchor-link'
    button.setAttribute('aria-label', 'Copy link to this section')
    button.innerHTML = `${LINK_ICON}<span class="anchor-tooltip" aria-hidden="true">Copied!</span>`

    let timer

    const showCopied = () => {
      button.classList.add('is-copied')
      clearTimeout(timer)
      timer = setTimeout(() => button.classList.remove('is-copied'), COPIED_RESET_MS)
    }

    const onClick = async (event) => {
      event.preventDefault()
      const url = `${baseUrl}#${heading.id}`
      try {
        await navigator.clipboard?.writeText(url)
      } catch {
        // clipboard unavailable/denied — still surface feedback to the user
      }
      showCopied()
    }

    button.addEventListener('click', onClick)
    heading.insertBefore(button, heading.firstChild)

    cleanups.push(() => {
      clearTimeout(timer)
      button.removeEventListener('click', onClick)
      button.remove()
      delete heading.dataset.anchorEnhanced
    })
  })

  return () => cleanups.forEach((fn) => fn())
}
